import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const ClientForm = ({ client, onSave, onCancel, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    rfidId: '',
    textIdentifier: '',
    photo: '',
    status: 'active',
    notes: ''
  });
  
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);

  useEffect(() => {
    if (client && isEditing) {
      setFormData({
        name: client?.name || '',
        dateOfBirth: client?.dateOfBirth || '',
        rfidId: client?.rfidId || '',
        textIdentifier: client?.textIdentifier || '',
        photo: client?.photo || '',
        status: client?.status || 'active',
        notes: client?.notes || ''
      });
      setPhotoPreview(client?.photo);
    }
  }, [client, isEditing]);

  const statusOptions = [
    { value: 'active', label: 'Active', description: 'Client can access training sessions' },
    { value: 'inactive', label: 'Inactive', description: 'Client temporarily suspended' },
    { value: 'suspended', label: 'Suspended', description: 'Client access restricted' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    } else if (formData?.name?.trim()?.length < 2) {
      newErrors.name = 'Name must be at least 2 characters';
    }

    if (!formData?.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    } else {
      const birthDate = new Date(formData.dateOfBirth);
      const today = new Date();
      let age = today?.getFullYear() - birthDate?.getFullYear();
      
      if (age < 18) {
        newErrors.dateOfBirth = 'Client must be at least 18 years old';
      } else if (age > 120) {
        newErrors.dateOfBirth = 'Please enter a valid date of birth';
      }
    }

    if (!formData?.rfidId?.trim()) {
      newErrors.rfidId = 'RFID ID is required';
    } else if (!/^[A-Z0-9]{8,12}$/?.test(formData?.rfidId?.trim())) {
      newErrors.rfidId = 'RFID ID must be 8-12 alphanumeric characters';
    }

    if (formData?.textIdentifier && formData?.textIdentifier?.length > 50) {
      newErrors.textIdentifier = 'Text identifier must be less than 50 characters';
    }

    if (formData?.notes && formData?.notes?.length > 500) {
      newErrors.notes = 'Notes must be less than 500 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    
    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handlePhotoUpload = (event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      if (file?.size > 5 * 1024 * 1024) { // 5MB limit
        setErrors(prev => ({
          ...prev,
          photo: 'Photo must be less than 5MB'
        }));
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        const photoUrl = e?.target?.result;
        setPhotoPreview(photoUrl);
        handleInputChange('photo', photoUrl);
      };
      reader?.readAsDataURL(file);
    }
  };

  const generateRFIDId = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    let result = '';
    for (let i = 0; i < 10; i++) {
      result += chars?.charAt(Math.floor(Math.random() * chars?.length));
    }
    handleInputChange('rfidId', result);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    
    try {
      const clientData = {
        ...formData,
        id: client?.id || Date.now(),
        createdAt: client?.createdAt || new Date()?.toISOString(),
        updatedAt: new Date()?.toISOString()
      };
      
      await onSave(clientData);
    } catch (error) {
      console.error('Error saving client:', error);
      setErrors({ submit: 'Failed to save client. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const calculateAge = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today?.getFullYear() - birthDate?.getFullYear();
    const monthDiff = today?.getMonth() - birthDate?.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today?.getDate() < birthDate?.getDate())) {
      age--;
    }
    
    return age;
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-foreground">
            {isEditing ? 'Edit Client' : 'Add New Client'}
          </h2>
          <p className="text-sm text-muted-foreground">
            {isEditing ? 'Update client information and settings' : 'Create a new client profile for training management'}
          </p>
        </div>
        
        <Button
          variant="ghost"
          size="icon"
          onClick={onCancel}
          iconName="X"
          iconSize={20}
        />
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Photo Upload Section */}
        <div className="flex items-start space-x-6">
          <div className="flex-shrink-0">
            <div className="w-24 h-24 rounded-full overflow-hidden bg-muted border-2 border-dashed border-border">
              {photoPreview ? (
                <Image
                  src={photoPreview}
                  alt="Client photo preview"
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Icon name="Camera" size={24} className="text-muted-foreground" />
                </div>
              )}
            </div>
            
            <div className="mt-3 space-y-2">
              <input
                type="file"
                id="photo-upload"
                accept="image/*"
                onChange={handlePhotoUpload}
                className="hidden"
              />
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => document.getElementById('photo-upload')?.click()}
                iconName="Upload"
                iconPosition="left"
                iconSize={16}
                fullWidth
              >
                Upload Photo
              </Button>
              
              {photoPreview && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setPhotoPreview(null);
                    handleInputChange('photo', '');
                  }}
                  iconName="Trash2"
                  iconPosition="left"
                  iconSize={16}
                  fullWidth
                >
                  Remove
                </Button>
              )}
            </div>
          </div>

          {/* Basic Information */}
          <div className="flex-1 space-y-4">
            <Input
              label="Full Name"
              type="text"
              placeholder="Enter client's full name"
              value={formData?.name}
              onChange={(e) => handleInputChange('name', e?.target?.value)}
              error={errors?.name}
              required
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Input
                  label="Date of Birth"
                  type="date"
                  value={formData?.dateOfBirth}
                  onChange={(e) => handleInputChange('dateOfBirth', e?.target?.value)}
                  error={errors?.dateOfBirth}
                  required
                />
                {formData?.dateOfBirth && (
                  <p className="text-sm text-muted-foreground mt-1">
                    Age: {calculateAge(formData?.dateOfBirth)} years
                  </p>
                )}
              </div>

              <Select
                label="Status"
                options={statusOptions}
                value={formData?.status}
                onChange={(value) => handleInputChange('status', value)}
                required
              />
            </div>
          </div>
        </div>

        {/* RFID and Identifier Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <div className="flex items-end space-x-2">
              <div className="flex-1">
                <Input
                  label="RFID ID"
                  type="text"
                  placeholder="Enter or generate RFID ID"
                  value={formData?.rfidId}
                  onChange={(e) => handleInputChange('rfidId', e?.target?.value?.toUpperCase())}
                  error={errors?.rfidId}
                  required
                />
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={generateRFIDId}
                iconName="Shuffle"
                iconSize={16}
                className="mb-1"
              >
                Generate
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              8-12 alphanumeric characters (A-Z, 0-9)
            </p>
          </div>

          <Input
            label="Text Identifier (Optional)"
            type="text"
            placeholder="e.g., Room 101, Blue Card, etc."
            value={formData?.textIdentifier}
            onChange={(e) => handleInputChange('textIdentifier', e?.target?.value)}
            error={errors?.textIdentifier}
            description="Additional identifier to help staff recognize the client"
          />
        </div>

        {/* Notes Section */}
        <div>
          <label className="block text-sm font-medium text-foreground mb-2">
            Notes (Optional)
          </label>
          <textarea
            value={formData?.notes}
            onChange={(e) => handleInputChange('notes', e?.target?.value)}
            placeholder="Add any additional notes about the client..."
            rows={4}
            className="w-full px-3 py-2 border border-border rounded-lg bg-input text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-transparent resize-none"
            maxLength={500}
          />
          <div className="flex justify-between items-center mt-1">
            <p className="text-xs text-muted-foreground">
              Medical conditions, preferences, special requirements, etc.
            </p>
            <p className="text-xs text-muted-foreground">
              {formData?.notes?.length}/500
            </p>
          </div>
          {errors?.notes && (
            <p className="text-sm text-error mt-1">{errors?.notes}</p>
          )}
        </div>

        {/* Form Actions */}
        <div className="flex items-center justify-between pt-6 border-t border-border">
          <div>
            {errors?.submit && (
              <p className="text-sm text-error">{errors?.submit}</p>
            )}
          </div>
          
          <div className="flex items-center space-x-3">
            <Button
              type="button"
              variant="outline"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            
            <Button
              type="submit"
              loading={isSubmitting}
              iconName={isEditing ? "Save" : "UserPlus"}
              iconPosition="left"
              iconSize={16}
            >
              {isEditing ? 'Update Client' : 'Add Client'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default ClientForm;